/**
 * Safe localStorage utilities to handle storage failures gracefully
 * Prevents cascading errors when localStorage or IndexedDB is unavailable
 */

/**
 * Safely get an item from localStorage
 * Returns null if localStorage is unavailable or the item doesn't exist
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`[Storage] Failed to get item from localStorage: ${key}`, error);
    return null;
  }
}

/**
 * Safely set an item in localStorage
 * Returns true if successful, false if localStorage is unavailable
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`[Storage] Failed to set item in localStorage: ${key}`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 * Returns true if successful, false if localStorage is unavailable
 */
export function safeRemoveItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[Storage] Failed to remove item from localStorage: ${key}`, error);
    return false;
  }
}

/**
 * Check if localStorage is available
 * Tests both read and write access
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('[Storage] localStorage is not available', error);
    return false;
  }
}